import {
  browserTracingIntegration,
  feedbackIntegration,
  init,
  replayIntegration,
} from '@sentry/angular';
import { Environment } from '../nimmsta-commons-module.config';

type SentryEnvironmentConfig = {
  production: boolean;
  environment?: Environment;
};

type InitSentryOptions = {
  dsn: string;
  release: string;
  environment: SentryEnvironmentConfig;
  sendDefaultPii?: boolean;
};

const nimmstaDomains = [
  /^https:\/\/(?:[^/?#]+\.)?nimmsta\.com(?::\d+)?(?:[/?#]|$)/,
  /^https:\/\/(?:[^/?#]+\.)?nimmsta-test\.de(?::\d+)?(?:[/?#]|$)/,
];

export const SENTRY_FEEDBACK_TRANSLATION_KEYS = [
  'sentry_feedback.trigger_label',
  'sentry_feedback.trigger_aria_label',
  'sentry_feedback.cancel_button_label',
  'sentry_feedback.submit_button_label',
  'sentry_feedback.confirm_button_label',
  'sentry_feedback.form_title',
  'sentry_feedback.email_label',
  'sentry_feedback.email_placeholder',
  'sentry_feedback.message_label',
  'sentry_feedback.message_placeholder',
  'sentry_feedback.name_label',
  'sentry_feedback.name_placeholder',
  'sentry_feedback.success_message_text',
  'sentry_feedback.is_required_label',
  'sentry_feedback.add_screenshot_button_label',
  'sentry_feedback.remove_screenshot_button_label',
  'sentry_feedback.highlight_tool_text',
  'sentry_feedback.hide_tool_text',
  'sentry_feedback.remove_highlight_text',
  'sentry_feedback.error_empty_message_text',
  'sentry_feedback.error_no_client_text',
  'sentry_feedback.error_timeout_text',
  'sentry_feedback.error_forbidden_text',
  'sentry_feedback.error_generic_text',
];

export const initSentry = ({
  dsn,
  release,
  environment: appEnvironment,
  sendDefaultPii,
}: InitSentryOptions) => {
  init({
    dsn,
    release,
    environment: getSentryEnvironmentName(appEnvironment.environment),
    sendDefaultPii,

    integrations: [
      browserTracingIntegration({}),
      replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        networkDetailAllowUrls: nimmstaDomains,
        networkCaptureBodies: true,
      }),
      feedbackIntegration({
        autoInject: false,
        colorScheme: 'light',
        showBranding: false,
      }),
    ],
    beforeSend(event, hint) {
      // Filter out HTTP 0 errors
      if (
        hint?.originalException
        && typeof hint.originalException === 'string'
        && hint.originalException.includes('0 Unknown Error')
      ) {
        return null;
      }

      return event;
    },

    tracesSampleRate: appEnvironment.production ? 0.01 : 0.1,

    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      // 'localhost',
      /^https:\/\/backend.*\.nimmsta\.com/,
      /^https:\/\/backend.*\.nimmsta-test\.de/,
      /^https:\/\/api\.nimmsta\.com/,
      /^https:\/\/api\.nimmsta-test\.de/,
    ],

    // Session Replay
    replaysSessionSampleRate: appEnvironment.production ? 0.01 : 0.1,
    replaysOnErrorSampleRate: 0.01,
  });
};

const getSentryEnvironmentName = (environment?: Environment): string => {
  switch (environment) {
    case Environment.PROD: return 'production';
    case Environment.STAGING: return 'staging';
    case Environment.DEV: return 'development';
    default: return 'unknown';
  }
};

export function getSentryFeedbackText(translations: Record<string, string>) {
  return {
    triggerLabel: translations['sentry_feedback.trigger_label'],
    triggerAriaLabel: translations['sentry_feedback.trigger_aria_label'],
    cancelButtonLabel: translations['sentry_feedback.cancel_button_label'],
    submitButtonLabel: translations['sentry_feedback.submit_button_label'],
    confirmButtonLabel: translations['sentry_feedback.confirm_button_label'],
    formTitle: translations['sentry_feedback.form_title'],
    emailLabel: translations['sentry_feedback.email_label'],
    emailPlaceholder: translations['sentry_feedback.email_placeholder'],
    messageLabel: translations['sentry_feedback.message_label'],
    messagePlaceholder: translations['sentry_feedback.message_placeholder'],
    nameLabel: translations['sentry_feedback.name_label'],
    namePlaceholder: translations['sentry_feedback.name_placeholder'],
    successMessageText: translations['sentry_feedback.success_message_text'],
    isRequiredLabel: translations['sentry_feedback.is_required_label'],
    addScreenshotButtonLabel: translations['sentry_feedback.add_screenshot_button_label'],
    removeScreenshotButtonLabel: translations['sentry_feedback.remove_screenshot_button_label'],
    highlightToolText: translations['sentry_feedback.highlight_tool_text'],
    hideToolText: translations['sentry_feedback.hide_tool_text'],
    removeHighlightText: translations['sentry_feedback.remove_highlight_text'],
    errorEmptyMessageText: translations['sentry_feedback.error_empty_message_text'],
    errorNoClientText: translations['sentry_feedback.error_no_client_text'],
    errorTimeoutText: translations['sentry_feedback.error_timeout_text'],
    errorForbiddenText: translations['sentry_feedback.error_forbidden_text'],
    errorGenericText: translations['sentry_feedback.error_generic_text'],
  };
}
